import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form-v7';
import type { Resolver } from 'react-hook-form-v7';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, AlertDescription, Button, Checkbox, Input, Label, MultiSelect } from '@parrot-software/design-system';
import { ChevronRight, Info as InfoIcon, Loader2 } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { useStoresForBlock } from '@subscriptions/presentation/hooks';
import { SUBSCRIPTION_BLOCK_NAME_MAX_LENGTH, createSubscriptionBlockFormSchema } from './SubscriptionBlockForm.schema';
import type { SubscriptionBlockFormValues } from './SubscriptionBlockForm.schema';
import { SubscriptionBlockFormSkeleton } from './SubscriptionBlockFormSkeleton';

type SubscriptionBlockFormProps = {
  defaultValues?: Partial<SubscriptionBlockFormValues>;
  isSubmitting: boolean;
  onSubmit: (values: SubscriptionBlockFormValues) => Promise<void>;
  blockUuid?: string;
  onCancel?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
};

/**
 * Pure form component that exposes the subscription block fields:
 * a free-text name and a multi-select of stores. Title, description and the
 * submit label/handler are owned by the parent container (wizard step,
 * future edit sheet, etc.).
 *
 * The store options are type-locked: once the user picks one store, options
 * with a different `interval` or `status` become disabled. Clearing
 * the selection unlocks all options. The "select all" affordance is hidden
 * since all selected stores must share both dimensions.
 *
 * The submit button is disabled while the form is invalid or while
 * `isSubmitting` is `true` (controlled by the parent based on the mutation
 * state).
 *
 * When `onDirtyChange` is provided, the parent is notified every time the
 * form's dirty state (`formState.isDirty`) changes — used by the edit sheet
 * to guard the close action against unsaved changes.
 *
 * @param props - Component properties (see `SubscriptionBlockFormProps`)
 * @returns React element rendering the form
 */
export const SubscriptionBlockForm = ({
  defaultValues,
  isSubmitting,
  onSubmit,
  blockUuid,
  onCancel,
  onDirtyChange,
}: SubscriptionBlockFormProps) => {
  const { t } = useTranslation('subscriptions');
  const { data: stores, isLoading: isLoadingStores } = useStoresForBlock(blockUuid);
  const isEdition = Boolean(blockUuid);

  const storesList = useMemo(() => stores ?? [], [stores]);

  const schema = useMemo(() => createSubscriptionBlockFormSchema(t, storesList, isEdition), [t, storesList, isEdition]);
  const resolver = useMemo(() => (yupResolver(schema) as unknown) as Resolver<SubscriptionBlockFormValues>, [schema]);

  const methods = useForm<SubscriptionBlockFormValues>({
    defaultValues: {
      name: defaultValues?.name ?? '',
      storeUuids: defaultValues?.storeUuids ?? [],
      groupInvoice: defaultValues?.groupInvoice ?? false,
    },
    mode: 'onChange',
    resolver,
  });

  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    setValue,
  } = methods;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const selectedStoreUuids = useWatch({ control, name: 'storeUuids' });
  const showGroupInvoice = !isEdition || (selectedStoreUuids?.length ?? 0) >= 2;

  useEffect(() => {
    if (!showGroupInvoice) {
      setValue('groupInvoice', false);
    }
  }, [showGroupInvoice, setValue]);

  const lockedDimensions = useMemo(() => {
    const firstSelected = storesList.find((store) => selectedStoreUuids?.includes(store.uuid));
    if (!firstSelected) return null;
    return { interval: firstSelected.interval, status: firstSelected.status };
  }, [storesList, selectedStoreUuids]);

  const storeOptions = useMemo(
    () =>
      storesList.map((store) => ({
        disabled: lockedDimensions
          ? store.interval !== lockedDimensions.interval || store.status !== lockedDimensions.status
          : false,
        label: `${store.name} (${t(`enums.interval.${store.interval}`)} · ${t(`enums.status.${store.status}`)})`,
        value: store.uuid,
      })),
    [storesList, lockedDimensions, t],
  );

  const isSubmitDisabled = !isValid || isSubmitting;

  if (isLoadingStores) {
    return <SubscriptionBlockFormSkeleton isEdition={isEdition} />;
  }

  return (
    <FormProvider {...methods}>
      <form
        className={cn('flex flex-col gap-6', { 'min-h-0 flex-1': isEdition })}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <div
          className={cn('grid grid-cols-1 gap-4 md:grid-cols-2', {
            'min-h-0 flex-1 content-start md:grid-cols-1 overflow-y-auto scroll-pb-5 px-4 pb-5': isEdition,
          })}
        >
          <div className="flex flex-col gap-1">
            <Label className="text-sm font-medium text-foreground" htmlFor="subscription-block-name">
              {t('blockCreationWizard.create.fields.name')} <span className="text-destructive">*</span>
            </Label>
            <Input
              aria-invalid={Boolean(errors.name)}
              id="subscription-block-name"
              maxLength={SUBSCRIPTION_BLOCK_NAME_MAX_LENGTH}
              placeholder={t('blockCreationWizard.create.fields.namePlaceholder')}
              type="text"
              {...register('name')}
            />
            {errors.name?.message ? <p className="text-xs text-destructive">{errors.name.message}</p> : null}
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-sm font-medium text-foreground" htmlFor="subscription-block-stores">
              {t('blockCreationWizard.create.fields.stores')} <span className="text-destructive">*</span>
            </Label>
            <Controller
              control={control}
              name="storeUuids"
              render={({ field }) => (
                <MultiSelect
                  className="h-9 min-h-9"
                  defaultValue={field.value ?? []}
                  hideSelectAll
                  id="subscription-block-stores"
                  maxCount={5}
                  modalPopover
                  onValueChange={(values) => field.onChange(values)}
                  options={storeOptions}
                  placeholder={t('blockCreationWizard.create.fields.storesPlaceholder')}
                  popoverClassName="z-[1350]"
                  searchPlaceholder={t('blockCreationWizard.create.fields.storesSearchPlaceholder')}
                  searchable
                  singleLine
                />
              )}
            />
            {errors.storeUuids?.message ? (
              <p className="text-xs text-destructive">{errors.storeUuids.message}</p>
            ) : null}
          </div>

          {showGroupInvoice && (
            <div className="col-span-full mt-2">
              <Controller
                control={control}
                name="groupInvoice"
                render={({ field }) => (
                  <div className="flex items-start gap-3 rounded-lg border border-input p-4">
                    <Checkbox
                      checked={field.value ?? false}
                      className="mt-0.5 shrink-0"
                      id="subscription-block-group-invoice"
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                    <div className="flex flex-col gap-1">
                      <label
                        className="text-sm font-medium text-foreground cursor-pointer"
                        htmlFor="subscription-block-group-invoice"
                      >
                        {t('blockCreationWizard.create.fields.groupInvoice')}
                      </label>
                      <p className="text-sm text-muted-foreground">
                        {t('blockCreationWizard.create.fields.groupInvoiceDescription')}
                      </p>
                    </div>
                  </div>
                )}
              />
            </div>
          )}
        </div>

        {isEdition ? (
          <div className="flex shrink-0 flex-col gap-4 p-4 pt-0">
            <Alert className="bg-bg-info border-info text-info">
              <InfoIcon className="text-info! shrink-0" size={16} />
              <AlertDescription className="text-sm">{t('blockEdit.sheet.alert')}</AlertDescription>
            </Alert>

            <div className="flex flex-col gap-2">
              <Button className="w-full" disabled={isSubmitDisabled} type="submit">
                {t('blockEdit.sheet.submit')}
                {isSubmitting && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
              </Button>
              <Button className="w-full" onClick={onCancel} type="button" variant="outline">
                {t('blockEdit.sheet.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button className="w-full md:w-auto" disabled={isSubmitDisabled} type="submit">
              {t('blockCreationWizard.create.submit')}
              {isSubmitting ? (
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              ) : (
                <ChevronRight className="ml-2 h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </form>
    </FormProvider>
  );
};
